/**
 * Benchmark book v1 — TS mirror of
 * `modules/product-knowledge/knowledge/benchmark-book-v1.md` (the editorial
 * source of truth; divergence = bug HERE, not in the doc).
 *
 * The cold-start gallery (Phase 5, plan v2 §6 decision 2) ranks menu templates
 * on THESE priors only — no warehouse reads, no cross-project aggregates. Every
 * constant names its book entry so the `prior-based` chip can cite it
 * (AC-031.3). Kinds mirror the doc: `published` / `observation` / `editorial`
 * (unsourced — recalibrated by the first served pull) / `policy`.
 *
 * Moved here from `src/modules/funnel-insights/benchmarkBook.ts` (2026-09-17)
 * unchanged, so the public and the admin calculator cite the SAME priors.
 *
 * Pure: imports only its own siblings in this directory.
 */

import {
  BENCHMARK_CHAIN,
  BENCHMARK_TRAFFIC_PER_DAY,
  SOLO_PRIOR_BY_FAMILY,
  TRIAL_CONV_PRIOR,
  TRIAL_MATURITY_SLACK_DAYS,
  TRIAL_SOLO_PRIOR,
  type ChainState,
} from './model';
import { PROCESSOR_SCHEME, STORE_SCHEME, sameScheme, type FeeScheme } from './deductions';

export type BookEntryKind = 'published' | 'observation' | 'editorial' | 'policy';

export interface BookEntry {
  id: string;
  title: string;
  kind: BookEntryKind;
  /** One line: report + section, or the calculator constant it mirrors. */
  source: string;
  date: string;
}

/** Prior band for a rate — the measurement plan sweeps P90 (early) → P10 (late). */
export interface PriorBand {
  p10: number;
  p50: number;
  p90: number;
}

export const BENCHMARK_BOOK_VERSION = 'benchmark-book/v2 (2026-09-22)';

/** The entries the gallery can cite (ids match the doc tables A–G). */
export const BENCHMARK_BOOK_ENTRIES: Record<string, BookEntry> = {
  'BB-01': { id: 'BB-01', title: 'Cost per start', kind: 'editorial', source: 'calculator default BENCHMARK_CHAIN.cps', date: '2026-08-31' },
  'BB-02': { id: 'BB-02', title: 'First → second screen (p1)', kind: 'editorial', source: 'calculator default BENCHMARK_CHAIN.p1', date: '2026-08-31' },
  'BB-03': { id: 'BB-03', title: 'Second screen → paywall (p2)', kind: 'editorial', source: 'calculator default BENCHMARK_CHAIN.p2', date: '2026-08-31' },
  'BB-04': { id: 'BB-04', title: 'Paywall → pressed buy (p3)', kind: 'editorial', source: 'calculator default BENCHMARK_CHAIN.p3; consistent with RevenueCat 2025 hard-paywall D35 median 12.1 %', date: '2026-08-31' },
  'BB-05': { id: 'BB-05', title: 'Monthly solo anchor', kind: 'editorial', source: 'SOLO_PRIOR_BY_FAMILY.monthly', date: '2026-08-31' },
  'BB-06': { id: 'BB-06', title: 'Annual solo anchor', kind: 'editorial', source: 'SOLO_PRIOR_BY_FAMILY.annual', date: '2026-08-31' },
  'BB-07': { id: 'BB-07', title: 'Weekly solo anchor', kind: 'editorial', source: 'SOLO_PRIOR_BY_FAMILY.weekly', date: '2026-08-31' },
  'BB-08': { id: 'BB-08', title: 'Trial-bearing offer solo anchor', kind: 'editorial', source: 'TRIAL_SOLO_PRIOR', date: '2026-08-31' },
  'BB-24': { id: 'BB-24', title: 'Premium-tier solo anchor', kind: 'editorial', source: 'half of the monthly solo for the ×1.75 price (unsourced)', date: '2026-09-04' },
  'BB-09': { id: 'BB-09', title: 'Nested-logit λ', kind: 'editorial', source: 'NEST_LAMBDA', date: '2026-08-31' },
  'BB-10': { id: 'BB-10', title: 'Trial → paid (global)', kind: 'editorial', source: 'blend: RevenueCat 2025 global median ≈ 38 % D35 · Runout observation 19/25', date: '2026-09-04' },
  'BB-11': { id: 'BB-11', title: 'Trial maturity slack', kind: 'editorial', source: 'TRIAL_MATURITY_SLACK_DAYS', date: '2026-08-31' },
  'BB-12': { id: 'BB-12', title: 'Monthly first renewal (r1)', kind: 'published', source: 'Adapty In-App Subscription Benchmarks 2026: 1st renewal 59.2 % global (all durations)', date: '2026-09-04' },
  'BB-13': { id: 'BB-13', title: 'Monthly second renewal (r2)', kind: 'editorial', source: 'pooled ≈ 0.68 reproduces CHECK_DEFAULT_BY_FAMILY.monthly 2.86 given r1 0.60 (payback.ts solveSecondRenewal, reconciled 2026-09-21)', date: '2026-09-21' },
  'BB-14': { id: 'BB-14', title: 'Weekly renewals (r1 / r2)', kind: 'editorial', source: 'derived from CHECK_DEFAULT_BY_FAMILY.weekly 5.36 over 52 cycles', date: '2026-09-04' },
  'BB-15': { id: 'BB-15', title: 'Annual: upfront at horizon, year-2 renewal not published', kind: 'published', source: 'RevenueCat 2025 (qualitative annual churn only)', date: '2026-09-04' },
  'BB-16': { id: 'BB-16', title: 'Prior strength (pseudo-counts)', kind: 'editorial', source: '200 paywall visitors · 100 subscribers per gate · 40 trial starters', date: '2026-09-04' },
  'BB-17': { id: 'BB-17', title: 'Price ladder from one anchor', kind: 'editorial', source: 'deriveLadderFromAnchor (AC-003)', date: '2026-08-31' },
  'BB-18': { id: 'BB-18', title: 'Default expected traffic', kind: 'editorial', source: 'BENCHMARK_TRAFFIC_PER_DAY', date: '2026-08-31' },
  'BB-19': { id: 'BB-19', title: 'Planner unlock', kind: 'policy', source: 'Phase-1 readiness classifier: 100 paywall visitors OR 25 conversions', date: '2026-08-31' },
  'BB-20': { id: 'BB-20', title: 'Choice rates solid', kind: 'policy', source: 'research stat tier floor: 300 pressed', date: '2026-08-31' },
  'BB-21': { id: 'BB-21', title: 'Trial verdict', kind: 'policy', source: '300 trial starts + trial days + slack (E6 quote grammar)', date: '2026-08-31' },
  'BB-22': { id: 'BB-22', title: 'Warehouse day-lag', kind: 'observation', source: 'marts materialize daily; events_deduped lags events_raw', date: '2026-09-04' },
  'BB-23': { id: 'BB-23', title: 'Annual renewals observable', kind: 'policy', source: 'cohort age ≥ 12 months → month 13', date: '2026-08-31' },
  'BB-C0': { id: 'BB-C0', title: 'Category: other / general', kind: 'policy', source: 'global priors', date: '2026-09-04' },
  'BB-C1': { id: 'BB-C1', title: 'Category: health & fitness — trial → paid 0.40', kind: 'published', source: 'RevenueCat 2025 trial conversion by category: Health & Fitness median 39.9 %', date: '2026-09-04' },
  'BB-C2': { id: 'BB-C2', title: 'Category: business & productivity — weekly solo × 1.5', kind: 'editorial', source: 'Adapty 2025: weekly plans > 50 % of revenue in Productivity (share published, multiplier editorial)', date: '2026-09-04' },
  'BB-C3': { id: 'BB-C3', title: 'Category: education — no category figure published', kind: 'published', source: 'absence in RevenueCat 2025 / Adapty 2025', date: '2026-09-04' },
  'BB-C4': { id: 'BB-C4', title: 'Category: lifestyle — no category figure published', kind: 'published', source: 'absence in RevenueCat 2025 / Adapty 2025', date: '2026-09-04' },
  // H · Deductions (2026-09-21) — list prices read on the vendors' pricing pages that day.
  'BB-25': { id: 'BB-25', title: 'Provider fee — card processor', kind: 'published', source: 'Stripe US pricing: 2.9% + $0.30 per successful domestic card charge (list price, read 2026-09-21)', date: '2026-09-21' },
  'BB-26': { id: 'BB-26', title: 'Provider fee — card processor + billing', kind: 'published', source: 'Stripe US pricing: cards 2.9% + $0.30 plus Billing pay-as-you-go 0.7% of volume (read 2026-09-21)', date: '2026-09-21' },
  'BB-27': { id: 'BB-27', title: 'Provider fee — merchant of record', kind: 'published', source: 'Paddle pricing: 5% + 50¢ per checkout transaction, all-inclusive (read 2026-09-21)', date: '2026-09-21' },
  'BB-28': { id: 'BB-28', title: 'Provider fee — app store, small business', kind: 'published', source: 'Apple Schedule 2 v126 (17 Dec 2025) §3.4(b): 15% for the App Store Small Business Program (< $1M proceeds prior calendar year); Google Play 15% on subscriptions (read 2026-09-22)', date: '2026-09-22' },
  'BB-29': { id: 'BB-29', title: 'Provider fee — app store, standard', kind: 'published', source: 'Apple Schedule 2 v126 (17 Dec 2025) §3.4(a): 30% of prices payable net of taxes collected (read 2026-09-22)', date: '2026-09-22' },
  'BB-30': { id: 'BB-30', title: 'Dispute line — industry "excessive" 0.75%', kind: 'published', source: 'Stripe docs, Measuring disputes: the card processing industry standard recognizes dispute activity above 0.75% as excessive (read 2026-09-21)', date: '2026-09-21' },
  'BB-31': { id: 'BB-31', title: 'Dispute line — Visa monitoring "Excessive" 1.5%', kind: 'published', source: 'Stripe docs, Dispute and fraud card monitoring programs: VAMP Excessive ≥1.5% and ≥1,500 in AP/CA/EU/US/LAC; count = TC15 disputes + TC40 fraud reports over captured payments (read 2026-09-21)', date: '2026-09-21' },
  'BB-32': { id: 'BB-32', title: 'Trial → paid by trial length, 2026', kind: 'published', source: 'RevenueCat State of Subscription Apps 2026: ≤4 days 25.5 %, 5–9 days 37.4 %, 17–32 days 42.5 % (read 2026-09-21)', date: '2026-09-21' },
  // H · Provider fee SCHEME (2026-09-22 plan, D2/D3) — how a store's commission differs from a processor's.
  // (dated 2026-09-22: this block was mislabelled "I ·" — the md mirror's H
  // is "Deductions", where BB-25..38 already live; its own I is "Trial
  // duration" (BB-32), a different topic — fixed in the same edit that added BB-39.)
  'BB-33': { id: 'BB-33', title: 'Store scheme — commission base is the price net of tax', kind: 'published', source: 'Apple Schedule 2 v126 §3.4(a) «the prices payable by End-Users shall be net of any and all taxes collected»; Exhibits 27 Aug 2026 (estimated VAT deducted prior to the commission for local developers) (read 2026-09-22)', date: '2026-09-22' },
  'BB-34': { id: 'BB-34', title: 'Store scheme — the commission comes back on a refund', kind: 'observation', source: 'Apple removed the «retain its commission notwithstanding the refund» clause (Schedule 2, June 2021); §6.3 says the developer reimburses «an amount equal to the price»; RevenueCat 2023 payment-report test: only the developer share is deducted (read 2026-09-22)', date: '2026-09-22' },
  'BB-35': { id: 'BB-35', title: 'Store scheme — a dispute is a refund, no developer fee', kind: 'published', source: 'Apple Schedule 2 v126 §6.3: a payment-provider refund notice is reimbursed at the price; no chargeback fee or rate threshold on the developer (read 2026-09-22)', date: '2026-09-22' },
  'BB-36': { id: 'BB-36', title: 'Store scheme — 15% after a year of paid service', kind: 'published', source: 'Apple Schedule 2 v126 §3.4(a): 15% on renewals after > 1 year of paid subscription service; lapses ≤ 60 days keep the tenure; free trials do not count (read 2026-09-22)', date: '2026-09-22' },
  'BB-37': { id: 'BB-37', title: 'Dispute fee — merchant of record', kind: 'published', source: 'Paddle help «Understanding chargebacks»: 20 USD/GBP/EUR or 40 CAD/AUD per chargeback, the transaction amount also leaves the balance (read 2026-09-22)', date: '2026-09-22' },
  'BB-38': { id: 'BB-38', title: 'Dispute fee — card processor', kind: 'published', source: 'Stripe US pricing: $15 per dispute (read 2026-09-22)', date: '2026-09-22' },
  'BB-39': { id: 'BB-39', title: 'Annual first renewal — countable on the 12-month basis', kind: 'policy', source: '§ D9 (2026-09-22 plan): due day 365, no published prior — reader-set', date: '2026-09-22' },
  'BB-40': { id: 'BB-40', title: 'Mechanic verdict', kind: 'policy', source: "300 in the mechanic's pool (a downsell's decliners, an upsell's buyers) — the BB-20 floor applied to a mechanic's acceptance (structural levers spec O-8)", date: '2026-09-28' },
};

// ---------------------------------------------------------------------------
// A · chain prior bands (BB-01..04) — P50 = the calculator defaults
// ---------------------------------------------------------------------------

export const CHAIN_PRIOR_BANDS: Record<keyof ChainState, PriorBand> = {
  cps: { p10: 1.0, p50: BENCHMARK_CHAIN.cps, p90: 2.5 },
  p1: { p10: 0.2, p50: BENCHMARK_CHAIN.p1, p90: 0.4 },
  p2: { p10: 0.35, p50: BENCHMARK_CHAIN.p2, p90: 0.65 },
  p3: { p10: 0.25, p50: BENCHMARK_CHAIN.p3, p90: 0.45 },
};

// ---------------------------------------------------------------------------
// B/C · takes and trial (BB-05..10)
// ---------------------------------------------------------------------------

export const BOOK_SOLO_PRIORS = {
  monthly: SOLO_PRIOR_BY_FAMILY.monthly, // BB-05
  annual: SOLO_PRIOR_BY_FAMILY.annual, // BB-06
  weekly: SOLO_PRIOR_BY_FAMILY.weekly, // BB-07
  trial: TRIAL_SOLO_PRIOR, // BB-08
  premium: 0.15, // BB-24
} as const;

export const BOOK_TRIAL_TO_PAID = TRIAL_CONV_PRIOR; // BB-10
export const BOOK_TRIAL_MATURITY_SLACK_DAYS = TRIAL_MATURITY_SLACK_DAYS; // BB-11
export const BOOK_TRIAL_DAYS = 7;

/** BB-32 — RevenueCat State of Subscription Apps 2026, trial → paid by trial length (days). */
export const BOOK_TRIAL_TO_PAID_BY_DAYS = [
  { minDays: 1, maxDays: 4, rate: 0.255 },
  { minDays: 5, maxDays: 9, rate: 0.374 },
  { minDays: 17, maxDays: 32, rate: 0.425 },
] as const;

/** BB-32's rate for a trial of `days` days; `null` outside its three bands (the caller falls back to BB-10). */
export function bookTrialToPaidForDays(days: number): number | null {
  return BOOK_TRIAL_TO_PAID_BY_DAYS.find((band) => days >= band.minDays && days <= band.maxDays)?.rate ?? null;
}

// ---------------------------------------------------------------------------
// D · renewal priors (BB-12..16) — rates + pseudo-counts (band width)
// ---------------------------------------------------------------------------

export const BOOK_MONTHLY_RENEWAL = { r1: 0.6, r2: 0.68 } as const; // BB-12 / BB-13
export const BOOK_WEEKLY_RENEWAL = { r1: 0.78, r2: 0.84 } as const; // BB-14

/** BB-16 — the only knobs that set the P10–P90 width of the gallery. */
export const BOOK_PRIOR_STRENGTH = {
  paywallVisitorsPerMenu: 200,
  subscribersPerRenewalGate: 100,
  trialStarters: 40,
} as const;

// ---------------------------------------------------------------------------
// E/F · ladder default + measurement thresholds (BB-17..23)
// ---------------------------------------------------------------------------

export const BOOK_DEFAULT_TRAFFIC_PER_DAY = BENCHMARK_TRAFFIC_PER_DAY; // BB-18

export const BOOK_THRESHOLDS = {
  unlockPaywallVisitors: 100, // BB-19
  unlockConversions: 25, // BB-19
  solidPressed: 300, // BB-20
  trialVerdictStarts: 300, // BB-21
  mechanicVerdictPool: 300, // BB-40
  warehouseLagDays: 1, // BB-22
  annualRenewalMonth: 13, // BB-23
  weeklyFirstRenewalDay: 7, // BB-14 cycle length
} as const;

// ---------------------------------------------------------------------------
// H · deductions — fee presets, dispute lines, and the scheme + dispute-fee
// number behind each preset (BB-25..38, D-A1/D1/D2)
// ---------------------------------------------------------------------------

/** A provider-fee preset the Advanced fold offers — a dated list price, never a default (D-A1). */
export interface FeePreset {
  id: string;
  /** Vendor-named (owner decision D-10, 2026-09-22 — superseded the earlier generic-on-purpose rule). */
  label: string;
  pct: number;
  fixed: number;
  entryId: string;
  /** How this provider's fee behaves (D1) — copied onto the stated fee by `applyFeePreset`, kept through edits. */
  scheme: FeeScheme;
  /**
   * The fee the provider lists per chargeback. `null` = the store carries
   * disputes (no fee of its own, D3). Fills `disputes.fee` when that row is
   * first stated while this preset is active, AND — since the 2026-09-22
   * provider-scheme plan's `[R3]` (superseding the earlier D-3 seed/chip
   * design) — `applyFeePreset` OVERWRITES a stated dispute fee with this
   * number too, exactly like the percent and the fixed part. It is the
   * provider's own number now, not a one-time seed the reader's edit
   * survives.
   */
  disputeFee: number | null;
  disputeFeeEntryId: string | null;
}

export const FEE_PRESETS: readonly FeePreset[] = [
  { id: 'card_processor', label: 'Stripe (2.9% + $0.30)', pct: 0.029, fixed: 0.3, entryId: 'BB-25', scheme: PROCESSOR_SCHEME, disputeFee: 15, disputeFeeEntryId: 'BB-38' },
  { id: 'card_processor_billing', label: 'Stripe + Billing (3.6% + $0.30)', pct: 0.036, fixed: 0.3, entryId: 'BB-26', scheme: PROCESSOR_SCHEME, disputeFee: 15, disputeFeeEntryId: 'BB-38' },
  { id: 'merchant_of_record', label: 'Paddle (5% + $0.50)', pct: 0.05, fixed: 0.5, entryId: 'BB-27', scheme: PROCESSOR_SCHEME, disputeFee: 20, disputeFeeEntryId: 'BB-37' },
  { id: 'app_store_small', label: 'App Store small business / Google Play (15%)', pct: 0.15, fixed: 0, entryId: 'BB-28', scheme: STORE_SCHEME, disputeFee: null, disputeFeeEntryId: null },
  { id: 'app_store', label: 'Apple App Store (30%)', pct: 0.3, fixed: 0, entryId: 'BB-29', scheme: { ...STORE_SCHEME, afterYearPct: 0.15 }, disputeFee: null, disputeFeeEntryId: null },
];

export const feePresetById = (id: string | null): FeePreset | null =>
  id === null ? null : (FEE_PRESETS.find(preset => preset.id === id) ?? null);

/**
 * The preset name a fee may keep (final fix wave M11, extended 2026-09-22 to
 * check the scheme too — D2): a preset NAME is a claim that the numbers AND
 * the behaviour are that book entry's, so it survives only while the id is
 * one the book knows, the fee still states that entry's own percent and
 * fixed part, AND its scheme still matches. An unknown id, numbers that moved
 * (a clamp, an edit, a hand-typed link), or a scheme that no longer matches,
 * keep the numbers and drop the name.
 */
export const presetIdFor = (preset: string | null, pct: number, fixed: number, scheme: FeeScheme): string | null => {
  const entry = feePresetById(preset);
  return entry !== null && Math.abs(entry.pct - pct) < 1e-9 && Math.abs(entry.fixed - fixed) < 1e-9 && sameScheme(entry.scheme, scheme)
    ? entry.id
    : null;
};

/** The two dispute-rate lines the exposure readout is drawn against (BB-30 / BB-31). */
export const DISPUTE_LINES = { industryExcessive: 0.0075, networkExcessive: 0.015 } as const;

// ---------------------------------------------------------------------------
// G · category modifiers (BB-C0..C4) — minimal, exactly as sourced
// ---------------------------------------------------------------------------

export type GalleryCategory =
  | 'other'
  | 'health_fitness'
  | 'education'
  | 'business_productivity'
  | 'lifestyle';

export interface CategoryModifier {
  category: GalleryCategory;
  label: string;
  entryId: string;
  /** Replaces BB-10 when set. */
  trialToPaid?: number;
  /** Multiplies the weekly solo anchor (BB-07) when set. */
  weeklySoloMultiplier?: number;
  /** Visible caption under the picker. */
  note: string;
}

export const CATEGORY_MODIFIERS: Record<GalleryCategory, CategoryModifier> = {
  other: {
    category: 'other',
    label: 'Other / general',
    entryId: 'BB-C0',
    note: 'global priors apply',
  },
  health_fitness: {
    category: 'health_fitness',
    label: 'Health & fitness',
    entryId: 'BB-C1',
    trialToPaid: 0.4,
    note: 'trial → paid prior 40 % (published category median, RevenueCat 2025)',
  },
  education: {
    category: 'education',
    label: 'Education / learning',
    entryId: 'BB-C3',
    note: 'no category-specific figure published — global priors apply',
  },
  business_productivity: {
    category: 'business_productivity',
    label: 'Business & productivity',
    entryId: 'BB-C2',
    weeklySoloMultiplier: 1.5,
    note: 'weekly solo × 1.5 (weekly plans carry > 50 % of Productivity revenue — Adapty 2025; the multiplier is editorial)',
  },
  lifestyle: {
    category: 'lifestyle',
    label: 'Lifestyle',
    entryId: 'BB-C4',
    note: 'no category-specific figure published — global priors apply',
  },
};

export const GALLERY_CATEGORY_ORDER: GalleryCategory[] = [
  'other',
  'health_fitness',
  'education',
  'business_productivity',
  'lifestyle',
];
